import type { CSSProperties, ReactNode } from 'react';
import { Fragment, useEffect, useMemo, useState } from 'react';

import ButtonIcon from '../Buttons/ButtonIcon';
import Icon from '../Icon';
import Loader from '../Loaders/LoaderSpinner';
import PaginationTable from '../Pagination/PaginationTable';
import Typo from '../Typography/Typo';

import cx from 'classnames';
import s from './Table.module.scss';

export type TableTheme = 'default' | 'card' | 'cardCol' | 'compact';
export type Justify = 'start' | 'center' | 'end' | 'left' | 'right';

export type Row = Record<string, any>;

export interface ITableCol<T extends Row = any> {
  align?: 'start' | 'end' | 'center';
  className?: string | ((row: T) => string | null);
  content: ReactNode | ((row: T, index: number) => ReactNode);
  disabled?: boolean | ((row: T) => boolean);
  hide?: boolean | (() => boolean);
  justify?: Justify;
  key: string;
  label?: ReactNode;
  noPadding?: boolean;
  size?: string;
  sortable?: boolean;
  hideable?: boolean;
}

export interface ITable<T extends Row> {
  cellHeight?: number;
  className?: string;
  classNameInner?: string;
  columns: ITableCol<T>[];
  data: T[];
  footer?: ReactNode | (() => ReactNode);
  header?: ReactNode | (() => ReactNode);
  id?: string;
  loading?: boolean;
  loadingTitle?: string;
  onChangePage?: (page: number) => void;
  onOpenChild?: (rows: Record<string, boolean>) => void;
  pagination?: {
    page?: number;
    limit?: number;
    hideOnSingle?: boolean;
    top?: boolean;
    bottom?: boolean;
  };
  rowUniqueKey?: (row: T, index?: number) => string;
  rowClassName?: string | ((row: T, index: number) => string | null);
  rowDisabled?: boolean | ((row: T, index: number) => boolean);
  rowTransparent?: (row: T, index: number) => boolean;
  rowSelected?: (row: T, index: number) => boolean;
  startOpen?: boolean;
  theme?: TableTheme;
}

const Table = <T extends Row>(props: ITable<T>) => {
  const {
    theme = 'default',
    columns,
    data,
    header,
    footer,
    cellHeight = 2.5,
    pagination = {},
    rowClassName,
    rowDisabled,
    startOpen,
    loading,
    loadingTitle,
    onChangePage,
    onOpenChild,
    // id,
    className,
    classNameInner,
    rowUniqueKey,
    rowTransparent,
    rowSelected,
  } = props;

  const getKey = (row: T, index?: number) => {
    if (typeof rowUniqueKey === 'function') {
      return rowUniqueKey(row, index);
    }
    return row.key || row.uid || row.id;
  };

  const { top = true, bottom = true, page = 1, limit = 0, hideOnSingle = false } = pagination;

  const enabled = limit > 0 && (top || bottom);

  const calc = (d: number, l: number) => {
    if (l <= 0) return d;
    return Math.ceil(d / l);
  };

  const [maxPages, setMaxPages] = useState(calc(data.length, limit));
  const [opens, setOpens] = useState<Record<string, boolean>>({});

  const handleSubrow = (rowId: string) => {
    setOpens((st) => {
      const news = { ...st, [rowId]: !st[rowId] };
      if (typeof onOpenChild === 'function') {
        onOpenChild(news);
      }
      return news;
    });
  };

  const [currentPage, setCurrentPage] = useState(page);

  const hasAnyChildren = useMemo(() => {
    return data.some((row) => Boolean(row.$children));
  }, [data]);

  const cols = useMemo(() => {
    if (!hasAnyChildren) return columns;
    const arrowCol: ITableCol<T> = {
      key: 'arrow',
      label: '',
      content: (row) => {
        if (!row.$children?.length) return null;
        const rowId = getKey(row);
        if (!rowId) {
          console.error('ONE OF key, id OR uid MUST BE SET');
        }
        const open = startOpen ? !opens[rowId] : opens[rowId];
        return (
          <ButtonIcon
            icon={open ? 'arrow_angle_up' : 'arrow_angle_down'}
            onClick={() => handleSubrow(rowId)}
            theme={'transparent'}
            size={'db'}
            color={'text'}
          />
        );
      },
      size: '3rem',
      justify: 'center',
      noPadding: true,
      sortable: false,
      hide: false,
      disabled: false,
      className: '',
    };
    return [arrowCol, ...columns];
  }, [columns, hasAnyChildren, opens]);

  const goToNextPage = () => {
    setCurrentPage((p) => {
      const np = Math.min(maxPages, p + 1);
      if (typeof onChangePage === 'function') {
        onChangePage(np);
      }
      return np;
    });
  };

  const goToPreviousPage = () => {
    setCurrentPage((p) => {
      const np = Math.max(1, p - 1);
      if (typeof onChangePage === 'function') {
        onChangePage(np);
      }
      return np;
    });
  };

  const getPaginatedData = () => {
    if (!enabled) return data;
    const startIndex = (currentPage - 1) * limit;
    const endIndex = startIndex + limit;
    return data.slice(startIndex, endIndex);
  };

  useEffect(() => {
    const max = calc(data.length, limit);
    setMaxPages(max);
    setCurrentPage(Math.min(max, page));
  }, [data.length]);

  const renderPagination = (bool) => {
    if (!enabled || !bool) return null;
    if (hideOnSingle && maxPages === 1) return null;
    return (
      <PaginationTable
        prevOnclick={() => goToPreviousPage()}
        nextOnclick={() => goToNextPage()}
        currentPage={currentPage}
        totalPage={maxPages}
      />
    );
  };

  const renderHeader = () => {
    if (!header && (!enabled || !top)) return null;
    return (
      <header className={s.header}>
        <div className={s.headerAction}>{typeof header === 'function' ? header() : header}</div>
        {renderPagination(top)}
      </header>
    );
  };

  const renderTableHeader = () => {
    if (theme === 'card' || theme === 'cardCol') return null;

    return cols.map((col, i) => {
      const hidden = Boolean(typeof col.hide === 'function' ? col.hide() : col.hide);
      // if (typeof col.hide === 'function') return col.hide();
      if (hidden) return null;

      const first = i === 0;
      const last = i === cols.length - 1;

      return (
        <div
          key={col.key}
          className={cx(s.headerCell, {
            [s.first]: first,
            [s.last]: last,
            [s.sortable]: col.sortable,
            [s.noPadding]: col.noPadding,
          })}
          style={
            {
              '--justify':
                col.justify === 'start' || col.justify === 'end'
                  ? `flex-${col.justify}`
                  : col.justify,
              '--text-align': col.justify === 'start' || col.justify === 'end' ? 'right' : 'center',
            } as CSSProperties
          }
        >
          {col.label}
          {col.sortable && <Icon className={s.sortableIcon} name={'arrow_angle_down'} />}
        </div>
      );
    });
  };

  const renderTableCells = (row: T, i: number) => {
    const _disabled = typeof rowDisabled === 'function' ? rowDisabled(row, i) : rowDisabled;
    const _transparent = typeof rowTransparent === 'function' ? rowTransparent(row, i) : false;
    const _selected = typeof rowSelected === 'function' ? rowSelected(row, i) : false;
    const _className = typeof rowClassName === 'function' ? rowClassName(row, i) : rowClassName;

    return cols.map((cell, k) => {
      // const hidden = typeof cell.hide === 'function' ? cell.hide() : false;
      const hidden = Boolean(typeof cell.hide === 'function' ? cell.hide() : cell.hide);
      if (hidden) return null;

      const first = k === 0;
      const last = k === cols.length - 1;
      const __disabled = typeof cell.disabled === 'function' ? cell.disabled(row) : cell.disabled;
      const __className =
        typeof cell.className === 'function' ? cell.className(row) : cell.className;
      return (
        <div
          className={cx(s.cell, _className, __className, {
            [s.first]: first,
            [s.last]: last,
            [s.noPadding]: cell.noPadding,
            [s.disabled]: _disabled || __disabled,
            [s.transparent]: _transparent,
            [s.selected]: _selected,
          })}
          key={`${i}-${cell.key}`}
          style={
            {
              '--align':
                cell.align === 'start' || cell.align === 'end' ? `flex-${cell.align}` : 'center',
              '--justify':
                cell.justify === 'start' || cell.justify === 'end'
                  ? `flex-${cell.justify}`
                  : cell.justify,
            } as CSSProperties
          }
        >
          {(theme === 'card' || theme === 'cardCol') && cell.label && (
            <label className={s.cellLabel}>{`${cell.label}:`}</label>
          )}
          {typeof cell.content === 'function' ? cell.content(row, i) : cell.content}
        </div>
      );
    });
  };

  const renderTableRows = () => {
    if (loading) return null;

    if (theme === 'card' || theme === 'cardCol') {
      return getPaginatedData().map((row, i) => {
        const id = getKey(row, i);
        return (
          <div key={id} className={s.row}>
            {renderTableCells(row, i)}
          </div>
        );
      });
    }

    return getPaginatedData().map((row, i) => {
      const rowId = getKey(row, i);
      let _show = rowId && opens[rowId];
      if (startOpen) {
        _show = rowId && !opens[rowId];
      }
      return (
        <Fragment key={rowId}>
          {renderTableCells(row, i)}
          {_show && row.$children?.map(renderTableCells)}
        </Fragment>
      );
    });
  };

  const renderLoader = () => {
    if (!loading) return null;
    return (
      <div className={s.wrapLoader}>
        <Loader className={s.loader} size={32} />
        {loadingTitle && <Typo text={loadingTitle} tag={'p'} size={'df'} />}
      </div>
    );
  };

  const renderFooter = () => {
    if (loading || (!footer && (!enabled || !bottom))) return null;
    return (
      <div className={s.footer}>
        {typeof footer === 'function' ? footer() : footer}
        {renderPagination(bottom)}
      </div>
    );
  };

  const renderContent = () => {
    if (loading) return renderLoader();
    return (
      <Fragment>
        {renderHeader()}
        <div
          className={cx(s.table, classNameInner)}
          style={
            {
              '--columns': cols
                .map((col) => {
                  const hidden = Boolean(typeof col.hide === 'function' ? col.hide() : col.hide);
                  return hidden ? '' : col.size || '1fr';
                })
                .join(' '),
              '--rows': pagination?.limit || data.length,
              '--h-cell': cellHeight,
            } as CSSProperties
          }
        >
          {renderTableHeader()}
          {renderTableRows()}
          {renderLoader()}
        </div>
        {renderFooter()}
      </Fragment>
    );
  };

  return <div className={cx(s.container, className, { [s[theme]]: true })}>{renderContent()}</div>;
};

export default Table;
